import { useCallback, useEffect, useRef } from 'react';
import { v7 as uuidv7 } from 'uuid';

import { isMplPreApprovalOffer } from 'shared/lib';
import type { TPreapprovalOfferStep } from 'shared/types/TRankingService';

import { useSendOffer } from 'features/OfferControls';

import type { TPreApprovalControlOffer } from './types';

type Params = {
  offer: TPreApprovalControlOffer;
  isEnabled: boolean;
  currentStep?: TPreapprovalOfferStep;
  onApplicationStepSubmit: () => void;
};

export const useApplicationStep = ({ isEnabled, currentStep, onApplicationStepSubmit, offer }: Params) => {
  const hasCompletedRef = useRef(false);
  const { sendPreApprovalOffer, sendMplPreApprovalOffer, isLoading } = useSendOffer('application');

  const onSubmit = useCallback(async () => {
    const uuid = uuidv7();

    if (isMplPreApprovalOffer(offer)) {
      await sendMplPreApprovalOffer(offer, { uuid });
    } else {
      await sendPreApprovalOffer(offer, { uuid });
    }

    onApplicationStepSubmit();
  }, [offer, onApplicationStepSubmit, sendPreApprovalOffer, sendMplPreApprovalOffer]);

  useEffect(() => {
    if (!isEnabled || currentStep !== 'application' || hasCompletedRef.current) {
      return;
    }

    hasCompletedRef.current = true;
    onSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, currentStep]);

  return { isApplicationSending: isLoading };
};
